import React, { useEffect, useState } from 'react';
import { ExternalLink, Search, Filter, Loader2, Briefcase } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchJobs = async () => {
      try {
        const { data, error } = await supabase
          .from('applications')
          .select('*')
          .eq('user_id', user.id)
          .order('applied_at', { ascending: false });

        if (error) throw error;
        setJobs(data || []);
      } catch (err) {
        console.error('Error fetching applications:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [user]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'Interview': return 'bg-purple-100 text-purple-700';
      case 'Offered': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const statuses = ['All', 'Interview', 'Offered'];

  // Filter jobs based on search and status
  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = searchQuery === '' || 
      job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (job.location || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'All' || job.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Interviews & Offers</h1>
        <p className="mt-1 text-slate-500">Track your secured interviews and job offers here.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-4 items-center justify-between bg-slate-50">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search applications..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="relative w-full sm:w-auto">
            <button 
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 w-full sm:w-auto justify-center"
            >
              <Filter className="w-4 h-4" />
              {statusFilter === 'All' ? 'Filter' : statusFilter}
            </button>
            {showFilterMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-[150px]">
                {statuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => { setStatusFilter(status); setShowFilterMenu(false); }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors ${
                      statusFilter === status ? 'text-indigo-600 font-semibold bg-indigo-50' : 'text-slate-700'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredJobs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-white border-b border-slate-100 text-sm text-slate-500 font-medium">
                    <th className="p-4 whitespace-nowrap">Company</th>
                    <th className="p-4 whitespace-nowrap">Role</th>
                    <th className="p-4 whitespace-nowrap">Location</th>
                    <th className="p-4 whitespace-nowrap">Date Applied</th>
                    <th className="p-4 whitespace-nowrap">Status</th>
                    <th className="p-4 whitespace-nowrap text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="p-4 font-semibold text-slate-900 whitespace-nowrap">{job.company}</td>
                      <td className="p-4 text-slate-600 whitespace-nowrap">{job.role}</td>
                      <td className="p-4 text-slate-500 text-sm whitespace-nowrap">{job.location || '—'}</td>
                      <td className="p-4 text-slate-500 text-sm whitespace-nowrap">
                        {new Date(job.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        {job.job_url && (
                          <a 
                            href={job.job_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors inline-flex"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="p-4 border-t border-slate-100 bg-slate-50 text-center text-sm text-slate-500">
              Showing {filteredJobs.length} of {jobs.length} applications
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <Briefcase className="w-10 h-10 text-slate-400" />
            </div>
            <p className="text-lg font-semibold text-slate-700 mb-1">
              {jobs.length === 0 ? 'No interviews yet' : 'No matching records'}
            </p>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              {jobs.length === 0 
                ? 'Once we secure an interview or offer on your behalf, it will appear here. Remember, you can view all sent applications directly in your dedicated Gmail account.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
